import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import React, { useState, useRef, useEffect } from 'react';
import { Subject, FormState } from '../../Subsections/SalaryCalculator/types';

interface PillsProps {
  items: Subject[];
  selectedItem: string;
  setSelectedItem: (slug: string) => void;
  handleFieldChange: (fieldName: string, value: string) => void;
  fieldName?: string;
}

const Pills: React.FC<PillsProps> = ({
  items,
  selectedItem,
  setSelectedItem,
  handleFieldChange,
  fieldName = 'subject',
}) => {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const rowHeight = 42; // Approximate height of a pill row (pill height + gap), used to hide/show more pills
  const maxVisibleRows = 3;

  const [showViewMore, setShowViewMore] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.scrollHeight;
      setShowViewMore(height > rowHeight * maxVisibleRows);
    }
  }, [items]);

  // Reorder items with selected first, others after
  const orderedItems = React.useMemo(() => {
    // check if there are items
    if (!items || items.length === 0) return [];

    // Get object of selected item
    const selectedItemObj = items.find((item) => item.slug === selectedItem);

    // If no selected item is found, just return all the items
    if (!selectedItemObj) return items;

    // Return with selected item first, followed by all other items
    return [
      selectedItemObj,
      ...items.filter((item) => item.slug !== selectedItem),
    ];
  }, [items, selectedItem]);

  const handlePillClick = (subject: Subject) => {
    // Track the click event
    tagularEvent('ElementClicked', {
      correlationId: uuid(),
      webElement: {
        elementType: 'button',
        location: 'salary-calculator',
        name: 'salary-options',
        position: '',
        text: subject.name,
      },
    });

    // Update the component's internal state
    setSelectedItem(subject.slug);

    // update the form state with the selected slug
    handleFieldChange(fieldName, subject.slug);
  };

  return (
    <div className='relative mb-6'>
      <div
        ref={containerRef}
        className='flex flex-wrap gap-2'
        style={{
          maxHeight: expanded ? 'none' : `${rowHeight * maxVisibleRows}px`,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out',
        }}
      >
        {orderedItems &&
          orderedItems.map((subject) => (
            <button
              key={subject.id}
              className={`px-4 py-2 rounded-full font-body-12 
              ${selectedItem === subject.slug ? 'bg-primary-500 text-white' : 'bg-white text-body border border-gray-50'}
              ${selectedItem === subject.slug ? 'order-first' : ''}
            `}
              onClick={() => handlePillClick(subject)}
            >
              {subject.name}
            </button>
          ))}
      </div>

      {showViewMore && (
        <div className='flex justify-center mt-2'>
          <button
            onClick={() => setExpanded(!expanded)}
            className='text-primary-500 text-sm font-medium flex items-center hover:underline transition-colors focus:outline-none'
            aria-expanded={expanded}
          >
            {/* TODO: Update svg for existing icon */}
            {expanded ? (
              <>
                View Less
                <svg
                  className='w-4 h-4 ml-1'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 15l7-7 7 7'
                  />
                </svg>
              </>
            ) : (
              <>
                View More
                <svg
                  className='w-4 h-4 ml-1'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Pills;
